import {useEffect, useState} from 'react';
import {View, ActivityIndicator, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';

import Format from '~/lib/format';
import {TIRE_LOCATION_ORDER, TIRE_SOURCE} from '~/types/rob';

import sy from '~/styles';
import {
  Text,
  Pressable,
  Grouping,
  TireTooltip,
  ActionButton,
} from '~/components/controls';
import Actionsheet, {Title, Actions} from '~/components/controls/Actionsheet';
import withDimensions from '~/components/hoc/with-dimensions';
import {isRobTaskApproved, isRobTaskDenied} from '~/types/rob';

import ListIcon from '~/images/md-icons/list/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import ArchiveIcon from '~/images/md-icons/archive/materialicons/24px.svg';
import UnarchiveIcon from '~/images/md-icons/unarchive/materialicons/24px.svg';
import ThumbDownIcon from '~/images/md-icons/thumb_down/materialicons/24px.svg';
import ThumbUpIcon from '~/images/md-icons/thumb_up/materialicons/24px.svg';

import {SeasonIndicator, Profile} from './shared';

import TireActions from './actions';

const HEADER_HEIGHT_IN_PX = 70;
const FOOTER_HEIGHT_IN_PX = 50;
const DIVIDER_HEIGHT_IN_PX = 1;

const TireIcon = ({action, ...rest}) => {
  let icon;
  switch (action) {
    case 'Current_Replace':
    case 'Current_Remove':
    case 'Stored_ReplaceNew':
      icon = <DeleteIcon {...rest} />;
      break;
    case 'Current_Store':
    case 'Current_StoreWheel':
      icon = <ArchiveIcon {...rest} />;
      break;
    case 'Current_TireRepair':
      icon = <TireRepairIcon {...rest} />;
      break;
    case 'Stored_WheelChange':
    case 'Stored_TireChange':
    case 'Stored_TireChangeThirdParty':
      icon = <UnarchiveIcon {...rest} />;
      break;
    default:
      icon = <ListIcon {...rest} />;
      break;
  }

  return icon;
};

const Tires = ({sla, tires, onPress}) => {
  const profile_widths = tires.reduce((acc, item) => {
    acc[item.location] = item.attributes?.profile_width;
    return acc;
  }, {});

  return tires.map((tire, index, array) => {
    const {location, action, attributes} = tire;
    const {rob_tire_id, title, description, seasonIndicator} = attributes;

    const is_last = index === array.length - 1;

    return (
      <View style={[sy['flex-row']]} key={location}>
        <Profile
          style={[sy['p-4']]}
          location={tire.location}
          widths={profile_widths}
        />
        <View style={[sy['flex-1']]}>
          <View style={[sy['p-4'], sy['pl-0']]}>
            <Pressable
              style={[sy['flex-row'], sy['justify-between']]}
              onPress={() => onPress(location)}>
              <View style={[sy['flex-1']]}>
                <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
                  <View style={[sy['flex-row']]}>
                    <Text style={{minWidth: 21}}>{location}</Text>
                    <Text>
                      &nbsp;&middot;&nbsp;
                      {rob_tire_id ? title : 'Onbekend'}
                    </Text>
                  </View>
                  <SeasonIndicator indicator={seasonIndicator} />
                </View>
                <Text
                  style={[
                    sy.smallRegular,
                    sy['text-lightgray'],
                    sy.underline,
                    sy.truncate,
                  ]}>
                  {rob_tire_id ? description : 'Niet opgegeven'}
                </Text>
              </View>
              {rob_tire_id && (
                <TireIcon action={action} width={24} height={24} />
              )}
            </Pressable>
          </View>
          {!is_last && <Divider style={[{backgroundColor: '#f2f2f2'}]} />}
        </View>
      </View>
    );
  });
};

const NewTires = ({sla, tires, lessor, onPress}) => {
  const profile_widths = tires.reduce((acc, item) => {
    acc[item.location] = item.profile_width;
    return acc;
  }, {});

  return tires.map((tire, index, array) => {
    const {
      location,
      title,
      description,
      source,
      price,
      manufacturerSuggestedRetailPrice,
      discount,
      makeCode,
      seasonIndicator,
    } = tire;

    const brand_sla = sla?.brands?.find((item) => item.makeCode === makeCode);

    const is_last = index === array.length - 1;

    return (
      <View style={[sy['flex-row']]} key={location}>
        <Profile
          style={[sy['p-4']]}
          location={location}
          widths={profile_widths}
        />
        <View style={[sy['flex-1']]}>
          <View style={[sy['p-4'], sy['pl-0']]}>
            <View
              style={[
                sy['flex-row'],
                sy['justify-between'],
                sy['items-center'],
              ]}>
              <Pressable
                style={[sy['flex-1']]}
                onPress={() => onPress(location)}>
                <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
                  <View style={[sy['flex-row']]}>
                    <Text style={{minWidth: 21}}>{location}</Text>
                    <Text>
                      &nbsp;&middot;&nbsp;
                      {title}
                    </Text>
                  </View>
                  <SeasonIndicator indicator={seasonIndicator} />
                </View>
                <Text
                  style={[
                    sy.smallRegular,
                    sy['text-lightgray'],
                    sy.underline,
                    sy.truncate,
                  ]}>
                  {description}
                </Text>
              </Pressable>
              <View style={[sy['pl-2']]}>
                <Text style={sy.smallRegular}>
                  {[
                    TIRE_SOURCE.Reserve,
                    TIRE_SOURCE.LessorStock,
                    TIRE_SOURCE.ThirdParty,
                    TIRE_SOURCE.Private,
                  ].includes(source)
                    ? null
                    : Format.price(price - discount)}
                </Text>
                <View style={[sy['flex-row'], sy['justify-end']]}>
                  {[
                    TIRE_SOURCE.Reserve,
                    TIRE_SOURCE.LessorStock,
                    TIRE_SOURCE.ThirdParty,
                    TIRE_SOURCE.Private,
                  ].includes(source) ? null : (
                    <TireTooltip
                      lessor={lessor}
                      manufacturerSuggestedRetailPrice={
                        manufacturerSuggestedRetailPrice
                      }
                      order={brand_sla?.order}
                      discount={Format.round(
                        1 - discount / manufacturerSuggestedRetailPrice,
                        2,
                      )}
                    />
                  )}
                </View>
              </View>
            </View>
          </View>
          {!is_last && <Divider style={[{backgroundColor: '#f2f2f2'}]} />}
        </View>
      </View>
    );
  });
};

const TiresActionsheet = ({
  dimensions,
  sla,
  lessor,
  task,
  component,
  onDismiss,
}) => {
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});

  const [showActions, setShowActions] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setSelected(
        task.parts.reduce((acc, item) => {
          const {mounted: attributes, stored, ...new_tire} = item.attributes;

          acc[item.location] = {
            action: item.action,
            stored_part_action: item.stored_part_action,
            attributes,
            stored: stored ?? {},
            service_cost: item.service_cost,
            repairs: item.repairs,
            new_tire: new_tire?.new_rob_tire_id
              ? {
                  ...new_tire,
                  description: item.description,
                  discount: item.discount,
                  price: item.price,
                  title: item.number,
                }
              : null,
          };

          return acc;
        }, {}),
      );

      setLoading(false);
    };

    fetch();
  }, []);

  const onShowActions = (location) => {
    setShowActions({
      location,
    });
  };

  const mounted = Object.keys(selected)
    .sort(
      (a, b) => TIRE_LOCATION_ORDER.indexOf(a) - TIRE_LOCATION_ORDER.indexOf(b),
    )
    .map((location) => ({
      location,
      action: selected[location].action,
      attributes: selected[location].attributes,
    }));

  const stored = Object.keys(selected)
    .sort(
      (a, b) => TIRE_LOCATION_ORDER.indexOf(a) - TIRE_LOCATION_ORDER.indexOf(b),
    )
    .filter((location) => selected[location].stored_part_action)
    .map((location) => ({
      location,
      action: selected[location].stored_part_action,
      attributes: selected[location].stored,
    }));

  const new_tires = Object.keys(selected)
    .sort(
      (a, b) => TIRE_LOCATION_ORDER.indexOf(a) - TIRE_LOCATION_ORDER.indexOf(b),
    )
    .filter((location) => selected[location].new_tire)
    .map((location) => ({
      location,
      ...selected[location].new_tire,
    }));

  if (showActions) {
    return (
      <TireActions
        sla={sla?.tires}
        location={showActions.location}
        tire={selected[showActions.location]}
        onDismiss={() => {
          setShowActions(null);
        }}
      />
    );
  }

  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        {loading && (
          <View style={[sy['p-4']]}>
            <ActivityIndicator size="large" color="#231fda" />
          </View>
        )}
        {!loading && (
          <>
            <Title style={[sy['justify-between']]}>
              <Text style={sy.largePlus}>Huidige banden</Text>
              {isRobTaskDenied(component.rob_component.status.code) ? (
                <ThumbDownIcon fill="#972727" width={24} height={24} />
              ) : isRobTaskApproved(component.rob_component.status.code) ? (
                <ThumbUpIcon fill="#299976" width={24} height={24} />
              ) : null}
            </Title>
            <Divider style={sy['bg-gray88']} />
            <ScrollView
              contentContainerStyle={{
                maxHeight:
                  dimensions.window.height -
                  HEADER_HEIGHT_IN_PX -
                  DIVIDER_HEIGHT_IN_PX -
                  FOOTER_HEIGHT_IN_PX -
                  DIVIDER_HEIGHT_IN_PX,
              }}>
              <Tires onPress={onShowActions} tires={mounted} />
              {new_tires.length > 0 && (
                <Grouping header="Nieuw" initial={{expand: true}}>
                  <NewTires
                    onPress={onShowActions}
                    tires={new_tires}
                    lessor={lessor}
                    sla={sla}
                  />
                </Grouping>
              )}
              {stored?.length > 0 && (
                <Grouping header="Uit opslag" initial={{expand: true}}>
                  <Tires onPress={onShowActions} tires={stored} />
                </Grouping>
              )}
            </ScrollView>
            <Divider style={sy['bg-gray88']} />
            <Actions>
              <ActionButton onPress={onDismiss}>OK</ActionButton>
            </Actions>
          </>
        )}
      </Actionsheet>
    </>
  );
};

export default withDimensions(TiresActionsheet);
